import styled from 'styled-components';
import { designTokens } from '../foundation/DesignTokens';

// Base input component
export const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border-radius: ${designTokens.radius.full};
  border: 1px solid ${designTokens.colors.gray[200]};
  font-size: 0.85rem;
  color: ${designTokens.colors.gray[800]};
  background: white;
  outline: none;
  transition: all 0.15s ease;
  font-family: ${designTokens.fonts.body};
  min-height: 38px;

  &::placeholder {
    color: ${designTokens.colors.gray[400]};
  }

  &:hover:not(:disabled) {
    border-color: ${designTokens.colors.gray[300]};
  }

  &:focus {
    border-color: ${designTokens.colors.primary[500]};
    box-shadow: 0 0 0 3px ${designTokens.colors.primary.ring};
    background: #F9FAFF;
  }

  &:disabled {
    background: ${designTokens.colors.gray[50]};
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (max-width: 900px) {
    font-size: 0.8rem;
    padding: 7px 11px;
    min-height: 36px;
  }
`;

// Search input variant
export const SearchInput = styled(Input)`
  min-width: 220px;
  padding-left: 38px; /* 12px (icon position) + 16px (icon size) + 10px (gap) */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='8'%3E%3C/circle%3E%3Cpath d='m21 21-4.35-4.35'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 12px center;
  background-size: 16px;

  @media (max-width: 900px) {
    min-width: 180px;
    padding-left: 36px;
  }

  @media (max-width: 600px) {
    min-width: 100%;
  }
`;

// Textarea variant
export const Textarea = styled.textarea`
  width: 100%;
  padding: ${designTokens.spacing.md} ${designTokens.spacing.lg};
  border-radius: ${designTokens.radius.md};
  border: 1px solid ${designTokens.colors.gray[200]};
  font-size: 0.85rem;
  color: ${designTokens.colors.gray[800]};
  background: white;
  outline: none;
  transition: all 0.15s ease;
  font-family: ${designTokens.fonts.body};
  resize: vertical;
  min-height: 100px;

  &::placeholder {
    color: ${designTokens.colors.gray[400]};
  }

  &:hover:not(:disabled) {
    border-color: ${designTokens.colors.gray[300]};
  }

  &:focus {
    border-color: ${designTokens.colors.primary[500]};
    box-shadow: 0 0 0 3px ${designTokens.colors.primary.ring};
    background: #F9FAFF;
  }

  &:disabled {
    background: ${designTokens.colors.gray[50]};
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (max-width: 900px) {
    font-size: 0.8rem;
    padding: ${designTokens.spacing.sm} ${designTokens.spacing.md};
  }
`;

// Select/dropdown variant
export const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border-radius: ${designTokens.radius.full};
  border: 1px solid ${designTokens.colors.gray[200]};
  font-size: 0.85rem;
  color: ${designTokens.colors.gray[800]};
  background: white;
  outline: none;
  transition: all 0.15s ease;
  font-family: ${designTokens.fonts.body};
  cursor: pointer;
  min-height: 38px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 36px;

  &:hover:not(:disabled) {
    border-color: ${designTokens.colors.gray[300]};
  }

  &:focus {
    border-color: ${designTokens.colors.primary[500]};
    box-shadow: 0 0 0 3px ${designTokens.colors.primary.ring};
    background-color: #F9FAFF;
  }

  &:disabled {
    background-color: ${designTokens.colors.gray[50]};
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (max-width: 900px) {
    font-size: 0.8rem;
    padding: 7px 11px;
    padding-right: 32px;
    min-height: 36px;
  }
`;

// Form label
export const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${designTokens.colors.gray[700]};
  margin-bottom: ${designTokens.spacing.sm};
  font-family: ${designTokens.fonts.body};

  @media (max-width: 900px) {
    font-size: 0.8rem;
  }
`;

// Form group wrapper
export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${designTokens.spacing.sm};
  margin-bottom: ${designTokens.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;

// Helper text for inputs
export const HelperText = styled.span`
  font-size: 0.75rem;
  color: ${designTokens.colors.gray[500]};
  line-height: 1.4;

  ${(props) =>
    props.error &&
    `
    color: #DC2626;
  `}

  @media (max-width: 900px) {
    font-size: 0.7rem;
  }
`;

export default Input;
